import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as Styled from '../../VHSharedInputArea.styled';
import { useSelector, useDispatch } from 'react-redux';
import {
  addUserChat,
  selectChatState,
  sendChat,
  ChatStates,
  isResponsiveSelector,
  sendUnresponsiveMessage,
  virtualHumanTalkingSelector,
  resendGreetingSelector,
} from '../../../../slices/chat/ChatSlice';
import { checkDiagnosis } from '../../../../slices/DiagnosisSlice';
import { virtualHumanDataSelector } from '../../../../slices/VirtualHumanSlice';
import VHTextChatInput from '../../../VHChatInputArea/VHTextChatInput/VHTextChatInput';
import { Icon } from '@virtidev/toolbox';
import { isForceDisabledSelector } from '@vh/slices/vh-player/VHPlayerSlice';
import { GetShouldGenerateAudio } from '@vh/utility/isVHAudioStreamService';
import { useTranslation } from 'react-i18next';

const VHDefaultChatInputArea = (props) => {
  const [chat, setChat] = useState('');
  // used for a specific segment of the chat that may change e.g. push to talk unfinished transcriptions
  const [appendPreviewChat, setAppendPreviewChat] = useState('');
  const handlePreviewChatChange = useCallback((newVal) => {
    setAppendPreviewChat(newVal);
  }, []);
  const isForceDisabled = useSelector(isForceDisabledSelector);
  const dispatch = useDispatch();
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const chatState = useSelector(selectChatState);
  const selectableOptions = useSelector(
    (state) => state.chat.selectableOptions
  );
  const checkDiagnosisStatus = useSelector(
    (state) => state.diagnosis.checkDiagnosisStatus
  );
  const completingSessionStatus = useSelector(
    (state) => state.chat.completingSessionStatus
  );
  const vh = useSelector(virtualHumanDataSelector);
  const isResponsive = useSelector(isResponsiveSelector);

  const resendGreeting = useSelector(resendGreetingSelector);
  const generateAudio = GetShouldGenerateAudio(
    vh?.VHVoice?.Service,
    vh.PlayerType
  );

  // submit given text as either chat or a diagnosis
  const submit = useCallback(
    (text) => {
      if (text === '') return;
      if (
        chatLoadingStatus === 'idle' &&
        completingSessionStatus === 'idle' &&
        checkDiagnosisStatus === 'idle'
      ) {
        dispatch(addUserChat({ text }));
        if (
          chatState === ChatStates.TALK ||
          chatState === ChatStates.AWAITING_FIRST_CHAT
        ) {
          const formData = {
            query: text,
            patientId: vh.ExternalID,
            aiServiceType: 2, // 1=openAI 2=azure
            generateAudio,
          };

          if (isResponsive) {
            dispatch(sendChat(formData));
          } else {
            dispatch(sendUnresponsiveMessage());
          }
        } else if (chatState === ChatStates.DIAGNOSIS) {
          const formData = {
            text,
            patientId: vh.ExternalID,
          };
          dispatch(checkDiagnosis(formData));
        }
        setChat('');
      }
    },
    [
      chatLoadingStatus,
      completingSessionStatus,
      checkDiagnosisStatus,
      dispatch,
      chatState,
      vh.ExternalID,
      generateAudio,
      isResponsive,
    ]
  );

  const [isAwaitingTranscriptionToSend, setIsAwaitingTranscriptionToSend] =
    useState(false);
  const [isPendingTranscription, setIsPendingTranscription] = useState(false);
  const handlePendingTranscription = useCallback((pending) => {
    setIsPendingTranscription(pending);
  }, []);

  const fullChat = chat + ' ' + appendPreviewChat;

  const handleSubmit = useCallback(() => {
    if (isPendingTranscription) {
      setIsAwaitingTranscriptionToSend(true);
    } else {
      submit(fullChat);
    }
  }, [submit, isPendingTranscription, fullChat]);

  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  let textDisabled =
    chatState === ChatStates.LOADING ||
    chatState === ChatStates.COMPLETE ||
    isForceDisabled;

  const submitDisabled =
    textDisabled ||
    virtualHumanTalking ||
    chatLoadingStatus === 'pending' ||
    chat === '' ||
    resendGreeting ||
    isForceDisabled ||
    isAwaitingTranscriptionToSend;

  const handleChange = useCallback(
    (newString) => {
      if (newString.indexOf('\n') > -1) {
        return;
      }
      // if we're awaitng a PTT transcription to finish (a hacky check based on the length of string change)
      const isMicTranscriptionChange = newString.length - chat.length > 1;
      if (isAwaitingTranscriptionToSend && isMicTranscriptionChange) {
        setIsAwaitingTranscriptionToSend(false);
        submit(newString);
      } else {
        setChat(newString);
      }
      setAppendPreviewChat('');
    },
    [chat.length, isAwaitingTranscriptionToSend, submit]
  );

  const { t } = useTranslation();

  let inputDisabledText = '';
  if (chatState === ChatStates.LOADING) {
    inputDisabledText = t('warmingUp');
  } else if (chatState === ChatStates.COMPLETE) {
    inputDisabledText = t('sessionComplete');
  }

  if (selectableOptions?.length > 0) {
    textDisabled = true;
    inputDisabledText = t('pleaseSelectResponseInLogsArea');
  }

  const recordingDisabled =
    textDisabled || virtualHumanTalking || chatLoadingStatus === 'pending';

  if (!virtualHumanTalking && chatState === ChatStates.COMPLETE) {
    return null;
  }

  return (
    <Styled.ChatInputWrapper $disabled={recordingDisabled}>
      <VHTextChatInput
        onChange={handleChange}
        onSubmit={submit}
        chat={chat}
        appendPreviewChat={appendPreviewChat}
        onPreviewChatChange={handlePreviewChatChange}
        textDisabled={textDisabled}
        inputDisabledText={inputDisabledText}
        submitDisabled={submitDisabled}
        recordingDisabled={recordingDisabled}
        maxLength={512}
        onPendingTranscription={handlePendingTranscription}
      />
      <Styled.ExtraWrapper>
        <Styled.SendButton
          color="turquoise"
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          <Icon icon="arrow" />
        </Styled.SendButton>
      </Styled.ExtraWrapper>
    </Styled.ChatInputWrapper>
  );
};

VHDefaultChatInputArea.propTypes = {};

export default VHDefaultChatInputArea;
